import { QueryKey, keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ReportItemKind } from "../utils/reporting/reportItemKinds";
import {
	addResponsibleNames,
	collectPeopleEmails,
	fetchEmailToNameMap,
} from "../utils/responsibleNames";

// Until a report's people are looked up, nobody has a name.
const NO_NAMES: Record<string, string> = {};

// The names of the people a report's items name: whoever is responsible for
// each item and, once recorded, whoever completed it. They are looked up in
// one batch and cached, for the page and its export. queryKey starts the
// lookup's key, which ends with the emails: the audit page's is shared with
// the drawer's findings list.
function useReportItemNames(
	queryKey: QueryKey,
	report: any,
	kind: ReportItemKind
) {
	const items = report?.[kind.listKey];
	const emails = useMemo(() => collectPeopleEmails(items), [items]);

	const {
		data: emailToName = NO_NAMES,
		isLoading,
		isPlaceholderData,
	} = useQuery({
		queryKey: [...queryKey, emails],
		queryFn: () => fetchEmailToNameMap(emails),
		enabled: emails.length > 0,
		staleTime: 5 * 60 * 1000,
		// A new completer changes the key; the names so far stay up meanwhile.
		placeholderData: keepPreviousData,
	});
	// Export waits for this report's own names. After Back or Forward the
	// previous report's stand in while they load; with no emails the lookup
	// never runs, and its stand-in would otherwise keep Export waiting.
	const isLoadingNames = isLoading || (isPlaceholderData && emails.length > 0);

	const reportWithNames = useMemo(() => {
		if (!report) return report;
		return {
			...report,
			[kind.listKey]: addResponsibleNames(
				Array.isArray(items) ? items : [],
				emailToName
			),
		};
	}, [report, items, kind.listKey, emailToName]);

	return { emailToName, reportWithNames, isLoadingNames };
}

export default useReportItemNames;
